import { useMemo, useState } from "react";
import { Reveal, SectionHead } from "@/components/Reveal";

const money = (n) => "$" + Math.round(n).toLocaleString("en-US");

export default function Calculator() {
  const [hours, setHours] = useState(35);
  const [rate, setRate] = useState(125);
  const [markup, setMarkup] = useState(40);

  const { labor, parts, atStake } = useMemo(() => {
    const laborAnnual = hours * rate * 50;
    const partsAnnual = hours * 45 * (1 + markup / 100) * 50;
    const total = laborAnnual + partsAnnual;
    return { labor: laborAnnual, parts: partsAnnual, atStake: total * 0.04 };
  }, [hours, rate, markup]);

  return (
    <section id="calculator" className="relative py-24 lg:py-32 blueprint-grid">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5">
          <SectionHead
            overline="Live Preview // The Profit Calculator"
            title="What is unsigned paperwork costing your shop?"
            sub="This is a working preview of the calculator inside the Shop Builder Kit. Industry recovery data puts revenue lost to unsigned authorizations, free diagnostics, and uncollected balances around 4% of gross."
          />
          <Reveal delay={0.15}>
            <div className="mt-8 border border-slate-400/10 bg-panel p-6 space-y-4">
              <div className="flex justify-between font-mono text-[11px] uppercase tracking-[0.18em] text-slate-500">
                <span>Annual labor revenue</span><span className="text-slate-200">{money(labor)}</span>
              </div>
              <div className="flex justify-between font-mono text-[11px] uppercase tracking-[0.18em] text-slate-500">
                <span>Annual parts revenue</span><span className="text-slate-200">{money(parts)}</span>
              </div>
              <div className="pt-4 border-t border-slate-400/10">
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-blaze">Revenue at risk without signed forms</p>
                <p data-testid="calc-result-protected-revenue" className="mt-1 font-mono font-bold tracking-tighter text-4xl sm:text-5xl text-blaze">
                  {money(atStake)}<span className="text-base text-slate-500">/yr</span>
                </p>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            <div className="clip-notch border border-slate-400/15 bg-panel p-8 lg:p-10 space-y-10">
              <div>
                <div className="flex justify-between mb-4">
                  <label htmlFor="calc-hours" className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">Billable tech hours / week</label>
                  <span className="font-mono font-bold text-slate-50">{hours} hrs</span>
                </div>
                <input id="calc-hours" data-testid="calc-tech-hours" type="range" min="10" max="60" value={hours}
                  onChange={(e) => setHours(+e.target.value)} />
              </div>
              <div>
                <div className="flex justify-between mb-4">
                  <label htmlFor="calc-rate" className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">Shop hourly rate</label>
                  <span className="font-mono font-bold text-slate-50">${rate}/hr</span>
                </div>
                <input id="calc-rate" data-testid="calc-hourly-rate" type="range" min="60" max="220" step="5" value={rate}
                  onChange={(e) => setRate(+e.target.value)} />
              </div>
              <div>
                <div className="flex justify-between mb-4">
                  <label htmlFor="calc-markup" className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">Parts markup</label>
                  <span className="font-mono font-bold text-slate-50">{markup}%</span>
                </div>
                <input id="calc-markup" data-testid="calc-markup-slider" type="range" min="10" max="100" step="5" value={markup}
                  onChange={(e) => setMarkup(+e.target.value)} />
              </div>
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
                The full Excel calculator adds overhead, break-even rate, and target-margin pricing.
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
